import { useState, useCallback } from 'react';
import { InvoiceWizardData, InvoiceItem, INVOICE_WIZARD_INITIAL_STATE } from '../types/invoiceWizard.types';
import { round2 } from '@/utils/format';

export function useInvoiceWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState<InvoiceWizardData>(INVOICE_WIZARD_INITIAL_STATE);

  const updateWizardData = useCallback((updates: Partial<InvoiceWizardData>) => {
    setWizardData((prev) => {
      const updated = { ...prev, ...updates };

      // Recalcular totales si se actualizaron los items, descuentos globales, acarreo o seguro
      if (updates.items || updates.globalDiscounts || updates.shippingCost !== undefined || updates.insuranceCost !== undefined) {
        return updateCalculations(updated);
      }

      return updated;
    });
  }, []);

  const addItem = useCallback((item: InvoiceItem) => {
    setWizardData((prev) => {
      const items = [...prev.items, item];
      return updateCalculations({ ...prev, items });
    });
  }, []);

  const updateItem = useCallback((index: number, item: InvoiceItem) => {
    setWizardData((prev) => {
      const items = [...prev.items];
      items[index] = item;
      return updateCalculations({ ...prev, items });
    });
  }, []);

  const removeItem = useCallback((index: number) => {
    setWizardData((prev) => {
      const items = prev.items.filter((_, i) => i !== index);
      return updateCalculations({ ...prev, items });
    });
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  }, []);

  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const resetWizard = useCallback(() => {
    setCurrentStep(0);
    setWizardData(INVOICE_WIZARD_INITIAL_STATE);
  }, []);

  return {
    currentStep,
    wizardData,
    updateWizardData,
    addItem,
    updateItem,
    removeItem,
    nextStep,
    previousStep,
    goToStep,
    resetWizard,
  };
}

function updateCalculations(data: InvoiceWizardData): InvoiceWizardData {
  // Subtotal sin impuestos (redondear para evitar errores de punto flotante)
  const subtotal = round2(data.items.reduce((sum, item) => sum + (item.subtotal || 0), 0));

  // Total de descuentos (usando unitDiscount)
  const totalDiscount = round2(data.items.reduce((sum, item) => {
    return sum + ((item.unitDiscount || 0) * (item.quantity || 0));
  }, 0));

  // Total de ITBMS
  const totalTax = round2(data.items.reduce((sum, item) => sum + (item.taxAmount || 0), 0));

  // Total de ISC
  const totalIsc = round2(data.items.reduce((sum, item) => sum + (item.iscAmount || 0), 0));

  // Total de OTI
  const totalOti = round2(data.items.reduce((sum, item) => sum + (item.otiAmount || 0), 0));

  // Agrupar y totalizar OTI por código (gOTITotal)
  const otiByCode = new Map<string, number>();

  data.items.forEach((item) => {
    if (item.gOTIItem && item.gOTIItem.length > 0) {
      item.gOTIItem.forEach((oti) => {
        const code = oti.dCodOTI;
        const value = parseFloat(oti.dValOTI) || 0;
        const currentTotal = otiByCode.get(code) || 0;
        otiByCode.set(code, currentTotal + value);
      });
    }
  });

  // Convertir Map a array de gOTITotal (redondear a 2 decimales al final, DGI: 1-11p2)
  const gOTITotal = Array.from(otiByCode.entries()).map(([code, total]) => ({
    dCodOTITotal: code,
    dValOTITotal: round2(total).toFixed(2),
  }));

  // Total de descuentos globales
  const totalGlobalDiscounts = round2((data.globalDiscounts || []).reduce((sum, disc) => sum + (disc.amount || 0), 0));

  // Asegurar que shippingCost e insuranceCost sean números válidos
  const shippingCost = Number(data.shippingCost) || 0;
  const insuranceCost = Number(data.insuranceCost) || 0;

  // dVTotItems: Suma de todos los item.total (DGI: 1-11p2)
  // Cada item.total ya incluye subtotal + impuestos + OTI + acarreo/seguro por item
  const vTotItems = round2(data.items.reduce((sum, item) => sum + (item.total || 0), 0));

  // Gran total per fórmula DGI: dVTot = dVTotItems + dTotAcar + dTotSeg + dTotOtrosGastos - dTotDesc
  // (acarreo/seguro global son separados del per-item que ya está en vTotItems)
  const grandTotal = round2(vTotItems + shippingCost + insuranceCost - totalGlobalDiscounts);

  return {
    ...data,
    subtotal,
    totalTax,
    totalDiscount,
    totalOti,
    totalIsc,
    gOTITotal: gOTITotal.length > 0 ? gOTITotal : undefined, // Solo incluir si hay OTI
    grandTotal,
  };
}
